import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import HTMLReactParser, { Text } from 'html-react-parser';

import { LogoFromGroupCode, Icon } from 'cosmos-components';

import { useWindowDimensions } from 'src/hooks/useWindowDimensions';
import { OnClickSignature } from 'src/components/shared/ContainedModal/ContainedModal';
import { useSci } from 'src/hooks/useSci';
import { sciLanguageSelector } from 'src/components/shared/SciPage/SciPage.selector';

import { Section, SectionId, SectionInput } from './documents.types';
import * as S from './documentRow.style';
import msg from './documents.messages';
import {
    ActionTags,
    GeneralTags,
    SectionInputValues,
    TagToSectionInputTypeMapping,
} from './documents.constants';

interface DocumentRowComponentProps {
    section: Section;
    error?: string;
    onValidateSection: (
        sectionId: SectionId,
        inputs: (SectionInput | null)[],
        renderedHtml: string | React.JSX.Element | React.JSX.Element[],
    ) => void;
}

const getNumberOfDocumentsToSignFromHtml = (section: Section): number => {
    let nummberOfDocumentsToSign = 0;

    HTMLReactParser(section.text, {
        replace: (domNode) => {
            if (domNode.type === 'text') {
                const textDomNode = domNode as Text;
                const type = textDomNode.data as ActionTags;

                if (
                    [ActionTags.ACCEPT_DECLINE, ActionTags.ACCEPT].includes(
                        type,
                    )
                ) {
                    nummberOfDocumentsToSign++;
                }
            }

            return domNode;
        },
    });

    return nummberOfDocumentsToSign;
};

export const DocumentRowComponent = ({
    section,
    error,
    onValidateSection,
}: DocumentRowComponentProps) => {
    const intl = useIntl();
    const { deviceType } = useWindowDimensions();
    const { data: sciData } = useSci();
    const isProcessing = useRef(false);
    const language = useSelector(sciLanguageSelector);

    const numberOfElementsToSignFromHtml =
        getNumberOfDocumentsToSignFromHtml(section);
    const numberOfElementsToSignFromSection =
        !!section.inputs && section.inputs.length;

    const [inputValues, setInputValues] = useState<(SectionInput | null)[]>(
        !!section.inputs &&
            numberOfElementsToSignFromSection &&
            numberOfElementsToSignFromSection === numberOfElementsToSignFromHtml
            ? section.inputs
            : Array.from(
                  { length: numberOfElementsToSignFromHtml },
                  () => null,
              ),
    );

    const signed = inputValues.every((a) => !!a);
    const signedFromBe =
        !!section.inputs &&
        section.inputs.every((input) => input.value !== null);

    const onClickGenerator = useCallback(
        (recordedIndex: number, status: SectionInput) => () =>
            setInputValues((prev) => {
                prev[recordedIndex] = status;
                return [...prev];
            }),
        [setInputValues],
    );

    const generateHTML = useCallback(() => {
        let index = 0;

        return HTMLReactParser(section.text, {
            replace: (domNode) => {
                if (domNode.type === 'text') {
                    const inputValue = inputValues[index];

                    if (
                        [ActionTags.ACCEPT_DECLINE, ActionTags.ACCEPT].includes(
                            domNode.data as ActionTags,
                        )
                    ) {
                        const isAccepted =
                            inputValue?.value === SectionInputValues.Accepted;
                        const isDeclined =
                            inputValue?.value === SectionInputValues.Declined;
                        const tag = domNode.data as ActionTags;

                        const element = (
                            <S.SectionButtonContainer deviceType={deviceType}>
                                <S.AcceptButton
                                    selected={isAccepted}
                                    onClick={onClickGenerator(index, {
                                        type: TagToSectionInputTypeMapping[tag],
                                        value: SectionInputValues.Accepted,
                                    })}
                                >
                                    <S.AcceptButtonText>
                                        {isAccepted && (
                                            <Icon name="done" color="white" />
                                        )}
                                        {isAccepted
                                            ? intl.formatMessage(
                                                  msg.buttons.accepted,
                                              )
                                            : intl.formatMessage(
                                                  msg.buttons.accept,
                                              )}
                                    </S.AcceptButtonText>
                                </S.AcceptButton>
                                {tag === ActionTags.ACCEPT_DECLINE && (
                                    <S.DeclineButton
                                        selected={isDeclined}
                                        onClick={onClickGenerator(index, {
                                            type: TagToSectionInputTypeMapping[
                                                tag
                                            ],
                                            value: SectionInputValues.Declined,
                                        })}
                                    >
                                        <S.AcceptButtonText>
                                            {isDeclined && (
                                                <Icon
                                                    name="cancelCircle"
                                                    color="white"
                                                />
                                            )}
                                            {isDeclined
                                                ? intl.formatMessage(
                                                      msg.buttons.declined,
                                                  )
                                                : intl.formatMessage(
                                                      msg.buttons.decline,
                                                  )}
                                        </S.AcceptButtonText>
                                    </S.DeclineButton>
                                )}
                            </S.SectionButtonContainer>
                        );

                        index++;

                        return element;
                    }
                    if (domNode.data === GeneralTags.PAGE_BREAK) {
                        return (
                            <S.EndOfPageContainer>
                                <S.DashedLine />
                                <S.EndOfPageText>
                                    {intl.formatMessage(
                                        msg.pageItems.endOfPage,
                                    )}
                                </S.EndOfPageText>
                                <S.DashedLine />
                            </S.EndOfPageContainer>
                        );
                    }
                }

                return domNode;
            },
        });
    }, [section, JSON.stringify(inputValues), deviceType, language]);

    const [generatedHtml, setGeneratedHtml] = useState(generateHTML());

    useEffect(() => {
        setGeneratedHtml(generateHTML());
    }, [JSON.stringify(inputValues), language]);

    const resetIfNotSubmited = useCallback(() => {
        isProcessing.current = false;
        if (!isProcessing.current) {
            setInputValues(
                !!section.inputs &&
                    numberOfElementsToSignFromSection &&
                    numberOfElementsToSignFromSection ===
                        numberOfElementsToSignFromHtml
                    ? section.inputs
                    : Array.from(
                          { length: numberOfElementsToSignFromHtml },
                          () => null,
                      ),
            );
        }
    }, [
        isProcessing.current,
        JSON.stringify(section.inputs),
        numberOfElementsToSignFromSection,
        numberOfElementsToSignFromHtml,
    ]);

    const getButtonComponent = useCallback(
        (onClick: OnClickSignature) => (
            <S.SignedButton
                onClick={(...params) => {
                    isProcessing.current = false;
                    onClick(...params);
                }}
                signed={signedFromBe}
            >
                {signedFromBe && <Icon name="done" color="white" />}
                <S.SignedButtonText>
                    {signedFromBe
                        ? intl.formatMessage(msg.buttons.accepted)
                        : intl.formatMessage(msg.buttons.review)}
                </S.SignedButtonText>
            </S.SignedButton>
        ),
        [signedFromBe, language],
    );

    const getDialogComponent = useCallback(
        (onClose: OnClickSignature) => (
            <div>
                <S.DocumentContainer>
                    <S.LogoReferenceContainer>
                        {sciData?.rental?.group?.code ? (
                            <S.LogoContainer>
                                <LogoFromGroupCode
                                    code={sciData.rental.group.code}
                                />
                            </S.LogoContainer>
                        ) : (
                            <></>
                        )}
                        <S.Reference>
                            {intl.formatMessage(msg.pageItems.reference, {
                                reference: sciData?.rental.reference,
                            })}
                        </S.Reference>
                    </S.LogoReferenceContainer>
                    {generatedHtml}
                </S.DocumentContainer>
                <S.AgreeToDocumentButtonContainer>
                    <S.AgreeToDocumentButton
                        disabled={!signed}
                        onClick={(e) => {
                            if (signed && !isProcessing.current) {
                                isProcessing.current = true;
                                onValidateSection(
                                    section.id,
                                    inputValues,
                                    generatedHtml,
                                );
                                onClose(e);
                            }
                        }}
                    >
                        <S.AgreeToDocumentButtonText>
                            {intl.formatMessage(msg.buttons.acceptDocument)}
                        </S.AgreeToDocumentButtonText>
                    </S.AgreeToDocumentButton>
                </S.AgreeToDocumentButtonContainer>
            </div>
        ),
        [
            signed,
            section,
            inputValues,
            generatedHtml,
            sciData?.rental.reference,
            sciData?.rental?.group?.code,
            language,
        ],
    );

    return (
        <>
            <S.Container>
                <S.Title>{section.name}</S.Title>
                <S.StyledContainedModal
                    type="button"
                    position="fixed"
                    hideClose
                    closable
                    afterClose={resetIfNotSubmited}
                    title={section.name}
                    getButtonComponent={getButtonComponent}
                    getDialogComponent={getDialogComponent}
                />
            </S.Container>
            {error && <S.ErrorSpan>{error}</S.ErrorSpan>}
            <S.DashedLineWithMargin />
        </>
    );
};
